import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { faScreencast } from '@fortawesome/pro-duotone-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';

import { PermissionStore } from '../../../../state/permissions.repository';
import { AppService } from '../../../services/app.service';
import { LocalStreamService } from '../../../services/local-stream.service';
import { UserService } from '../../../services/user.service';
import { SquareIconBtn } from '../../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';

/**
 * The dock's screen-share button: starts or stops the own screen share.
 * Every share is presented to the room (see UserService.setOwnScreenCast);
 * viewers can leave the presentation on their own tile.
 */
@Component({
  selector: 'andor-screen-share-button',
  standalone: true,
  imports: [TranslatePipe, FaDuotoneIconComponent, SquareIconBtn],
  templateUrl: './screen-share-button.html',
  styleUrl: './screen-share-button.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreenShareButton {
  private readonly localStream = inject(LocalStreamService);
  private readonly userService = inject(UserService);
  private readonly permissionStore = inject(PermissionStore);
  protected readonly appService = inject(AppService);

  protected readonly faScreencast = faScreencast;

  protected readonly sharing = toSignal(
    this.userService.ownScreenCast$.pipe(map((screenCast) => !!screenCast)),
    { initialValue: false }
  );
  private readonly noVideoPermission = toSignal(
    this.permissionStore.useCamera$.pipe(map((useCamera) => !useCamera)),
    { initialValue: false }
  );
  // `useCamera` gates screen sharing too. A running share must stay
  // stoppable, so only starting one is blocked.
  protected readonly blocked = computed(() => this.noVideoPermission() && !this.sharing());

  // Runs inside the click: getDisplayMedia needs that user gesture.
  protected async toggle() {
    if (this.sharing()) {
      this.localStream.stopScreenCastStream();
      return;
    }
    if (this.noVideoPermission()) return;
    await this.localStream.startScreenCastStream();
  }
}
